// PortalShell — one chrome shared by every role's dashboard, modeled
// on credentik-app/v2's shell + MemberMD V2's PortalShell.
//
// Layout:
//   - Sidebar: 240px on lg+, slide-out drawer below (Menu button in topbar).
//   - Topbar: 56px sticky. Page title + actions on the left, language
//     switcher + user menu on the right.
//   - Brand block top-left of the sidebar with the gradient mark.
//   - Section-grouped nav items with active state.
//   - User menu (top-right): logged-in name + role, "Sign out".
//
// Callers pass `nav` (per-role NavSection[]) + `activeId` (current item)
// + `title` (page title). The shell renders chrome; children render the page.

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, ChevronDown, ShoppingBag, UserCircle, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { catalog, type Me } from '../../lib/api';
import { auth as authApi } from '../../lib/api';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ViewOnPhoneButton } from './ViewOnPhoneButton';
import { cn } from '../../lib/cn';

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  badge?: number | string;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

interface Props {
  nav: NavSection[];
  activeId: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}

export function PortalShell({ nav, activeId, title, subtitle, actions, children }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--color-panel)]">
      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-[var(--color-ink)]/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-60 bg-white border-r border-[var(--color-border)] flex flex-col',
          'transform transition-transform lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <BrandBlock onClose={() => setDrawerOpen(false)} />
        <NavList nav={nav} activeId={activeId} onPick={() => setDrawerOpen(false)} />
        <UserMenu />
      </aside>

      {/* Main column */}
      <div className="lg:pl-60 flex flex-col min-h-screen">
        <TopBar
          title={title}
          actions={actions}
          onOpenDrawer={() => setDrawerOpen(true)}
        />
        {/* pb clears the fixed mobile bottom nav (hidden on lg+). */}
        <main className="flex-1 px-4 sm:px-6 py-6 pb-24 lg:pb-6">
          <div className="max-w-7xl mx-auto">
            {/* The page heading lives in the BODY, not the title bar.
                Web Portal v2 puts 28px/800 titles here and keeps the bar for
                chrome only; ours were 16px in the header, which is roughly
                half the intended size and above the content rather than
                leading it.
                Done inside the shell so all 39 call sites keep passing
                `title`/`subtitle` unchanged. The bar still shows the title on
                mobile, where a sticky label beats scrolling to find out where
                you are. */}
            <div className="hidden lg:block mb-6">
              <h1 className="text-[28px] font-extrabold tracking-[-0.8px] text-[var(--color-ink)]">{title}</h1>
              {subtitle && (
                <p className="mt-1 text-[14px] text-[var(--color-secondary-2)]">{subtitle}</p>
              )}
            </div>
            {children}
          </div>
        </main>
      </div>

      {/* Mobile bottom tab bar (app-like nav, MemberMD/ShiftPulse style) */}
      <MobileBottomNav nav={nav} activeId={activeId} />
    </div>
  );
}

// ── Mobile bottom nav ───────────────────────────────────────────────────
// The primary handful of destinations as a fixed bottom tab bar on phones.
// The full nav stays available via the slide-out drawer. Derived from the
// first up-to-5 nav items so each role gets its most-used screens.

function MobileBottomNav({ nav, activeId }: { nav: NavSection[]; activeId: string }) {
  const items = nav.flatMap((s) => s.items).slice(0, 5);
  if (items.length === 0) return null;
  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-[var(--color-border)] flex"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {items.map((it) => {
        const Active = it.id === activeId;
        return (
          <Link
            key={it.id}
            to={it.href}
            className={cn(
              'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors relative',
              Active ? 'text-brand-700' : 'text-[var(--color-secondary-2)]',
            )}
          >
            <span className="relative">
              <it.icon className={cn('w-5 h-5', Active ? 'text-brand-600' : 'text-[var(--color-muted)]')} />
              {it.badge != null && it.badge !== 0 && it.badge !== '0' && (
                <span className="absolute -top-1 -right-2 min-w-[14px] h-[14px] px-1 rounded-full bg-[var(--color-danger-fg)] text-white text-[9px] font-bold tabular-nums flex items-center justify-center">
                  {String(it.badge).length > 2 ? '99+' : it.badge}
                </span>
              )}
            </span>
            <span className="text-[10px] font-medium leading-none">{it.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

// ── Sidebar pieces ──────────────────────────────────────────────────────

/**
 * Which portal am I in?
 *
 * Four roles share one shell, and the wordmark alone said nothing about which
 * one you were looking at. The design puts a workspace label under it —
 * "Customer · US", "Merchant", "Super Admin" — which matters most for staff
 * who legitimately hold more than one account.
 */
function workspaceLabel(user: Me | null): string {
  switch (user?.role) {
    case 'customer': {
      const country = (user.customer_profile as { country?: string } | null)?.country;
      return country ? `Customer · ${country}` : 'Customer';
    }
    case 'recipient':
      return 'Recipient · Haiti';
    case 'merchant':
      return 'Merchant';
    case 'admin':
      return 'Admin';
    case 'super_admin':
      return 'Super Admin';
    default:
      return '';
  }
}

function BrandBlock({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const label = workspaceLabel(user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Stripe in test mode means no real card can be charged. An admin should
  // see that from any screen rather than discovering it from a support
  // ticket, so the design puts an environment chip beside the wordmark.
  const nonProd = import.meta.env.VITE_STRIPE_MODE !== 'live';

  return (
    <div className="h-16 px-4 flex items-center justify-between border-b border-[var(--color-hairline)] shrink-0">
      <Link to="/" className="flex items-center gap-2.5 min-w-0">
        <span className="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white">
          <ShoppingBag className="w-4 h-4" />
        </span>
        <span className="min-w-0">
          <span className="block font-semibold tracking-tight text-[15px] text-[var(--color-ink)]">VoyeLakay</span>
          {label && (
            <span className="block truncate text-[11px] font-semibold text-[var(--color-muted)]">{label}</span>
          )}
        </span>
        {isAdmin && nonProd && (
          <span className="ml-1 shrink-0 rounded-md border border-[var(--color-warning-border)] bg-[var(--color-warning-bg)] px-1.5 py-0.5 text-[10px] font-extrabold tracking-[0.6px] text-[var(--color-warning-fg)]">
            TEST
          </span>
        )}
      </Link>
      <button
        type="button"
        onClick={onClose}
        className="lg:hidden p-1.5 rounded-md hover:bg-[var(--color-panel)] text-[var(--color-secondary-2)]"
        aria-label="Close menu"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function NavList({ nav, activeId, onPick }: { nav: NavSection[]; activeId: string; onPick: () => void }) {
  return (
    <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-5">
      {nav.map((section, i) => (
        <div key={i}>
          {section.title && (
            <p className="px-2.5 mb-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-[var(--color-muted)]">{section.title}</p>
          )}
          <div className="space-y-0.5">
            {section.items.map((it) => {
              const Active = it.id === activeId;
              return (
                <Link
                  key={it.id}
                  to={it.href}
                  onClick={onPick}
                  className={cn(
                    'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] font-medium transition-colors',
                    Active
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-[var(--color-secondary)] hover:bg-[var(--color-panel)] hover:text-[var(--color-ink)]',
                  )}
                >
                  <it.icon className={cn('w-4 h-4', Active ? 'text-brand-600' : 'text-[var(--color-muted)]')} />
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.badge != null && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 text-white text-[10px] font-semibold">
                      {it.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

// Maps the auth'd user's role to the URL of their settings page so
// the user-menu "Profile & settings" link routes correctly per role.
function settingsPathFor(role: string | undefined): string {
  switch (role) {
    case 'admin':
    case 'super_admin': return '/admin/settings';
    case 'merchant':    return '/merchant/settings';
    case 'recipient':   return '/recipient/settings';
    case 'customer':
    default:            return '/customer/settings';
  }
}

function UserMenu() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const signOut = async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    await refresh();
    navigate('/login', { replace: true });
  };

  const displayName = user?.customer_profile && (user.customer_profile as { full_name?: string }).full_name
    ? (user.customer_profile as { full_name: string }).full_name
    : user?.merchant_profile && (user.merchant_profile as { business_name?: string }).business_name
    ? (user.merchant_profile as { business_name: string }).business_name
    : user?.admin_profile && (user.admin_profile as { display_name?: string }).display_name
    ? (user.admin_profile as { display_name: string }).display_name
    : user?.recipient_profile && (user.recipient_profile as { full_name?: string }).full_name
    ? (user.recipient_profile as { full_name: string }).full_name
    : user?.email ?? 'Account';

  return (
    <div className="border-t border-[var(--color-hairline)] p-3 relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-[var(--color-panel)]"
      >
        <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-[var(--color-ink)] text-white text-[12px] font-semibold shrink-0">
          {displayName.charAt(0).toUpperCase()}
        </span>
        <span className="flex-1 min-w-0 text-left">
          <span className="block text-[12.5px] font-semibold text-[var(--color-ink)] truncate">{displayName}</span>
          <span className="block text-[11px] text-[var(--color-secondary-2)] capitalize">{user?.role}</span>
        </span>
        <ChevronDown className={cn('w-3.5 h-3.5 text-[var(--color-muted)] transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-[var(--color-border)] rounded-lg shadow-lg py-1 z-50">
          <Link
            to={settingsPathFor(user?.role)}
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-[13px] text-[var(--color-body)] hover:bg-[var(--color-panel)]"
          >
            <UserCircle className="w-3.5 h-3.5 text-[var(--color-muted)]" /> Profile & settings
          </Link>
          <button
            type="button"
            onClick={signOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[var(--color-body)] hover:bg-[var(--color-panel)]"
          >
            <LogOut className="w-3.5 h-3.5 text-[var(--color-muted)]" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ── Top bar ─────────────────────────────────────────────────────────────

/**
 * The live HTG rate.
 *
 * Every quote locks to this number, and it was nowhere in the chrome — a
 * customer approving $41.20 had no way to see the rate it was struck at
 * without opening a quote. Desktop only: on a phone the title bar has no room
 * and the rate is shown on the quote itself.
 *
 * Renders nothing if the rate cannot be fetched. A stale or guessed FX figure
 * in persistent chrome would be worse than none — people would price against
 * it.
 */
function FxTicker() {
  const q = useQuery({
    queryKey: ['catalog', 'fx'],
    queryFn: catalog.fx,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const rate = q.data?.htg_per_usd;
  if (rate == null) return null;

  return (
    <span
      className="hidden xl:inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-2.5 py-1.5"
      title="Live exchange rate. Each quote locks the rate at the time it is sent."
    >
      <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-success-fg)]" aria-hidden="true" />
      <span className="font-mono text-[12px] text-[var(--color-body)] tabular-nums">
        1 USD = {rate} HTG
      </span>
    </span>
  );
}

/** Avatar → this role's settings. Mobile only; the sidebar covers desktop. */
function AccountButton() {
  const { user } = useAuth();
  if (!user) return null;

  const href =
    user.role === 'merchant' ? '/merchant/settings'
    : user.role === 'recipient' ? '/recipient/settings'
    : user.role === 'admin' || user.role === 'super_admin' ? '/admin/settings'
    : '/customer/settings';

  // Initials from the profile name where there is one, else the email — never
  // an empty circle.
  const source =
    (user.customer_profile as { full_name?: string } | null)?.full_name
    ?? (user.merchant_profile as { business_name?: string } | null)?.business_name
    ?? (user.recipient_profile as { full_name?: string } | null)?.full_name
    ?? user.email
    ?? '?';

  const initials = source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

  return (
    <Link
      to={href}
      aria-label="Account and settings"
      className="lg:hidden inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[12px] font-extrabold text-brand-700 hover:bg-brand-100 transition-colors"
    >
      {initials}
    </Link>
  );
}

function TopBar({ title, actions, onOpenDrawer }: { title: string; actions?: ReactNode; onOpenDrawer: () => void }) {
  const { t } = useTranslation();
  return (
    <header className="h-16 sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[var(--color-hairline)] flex items-center px-4 sm:px-6 gap-3">
      <button
        type="button"
        onClick={onOpenDrawer}
        className="lg:hidden p-2 -ml-2 rounded-md hover:bg-[var(--color-panel)] text-[var(--color-secondary)]"
        aria-label="Open menu"
      >
        <Menu className="w-4.5 h-4.5" />
      </button>
      <div className="min-w-0 flex-1">
        {/* Desktop shows the 28px heading in the body instead. */}
        <h1 className="lg:hidden text-[16px] font-semibold tracking-tight text-[var(--color-ink)] truncate">{title}</h1>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <FxTicker />
        {actions}
        <ViewOnPhoneButton />
        <LanguageSwitcher />
        {/* On a phone the account menu lives in the sidebar footer, which is
            behind the drawer — two taps and a scroll from anywhere. The
            Mobile v3 design puts an avatar in the title bar instead, so
            settings and sign-out are always one tap away. Hidden on desktop,
            where the sidebar is already on screen. */}
        <AccountButton />
      </div>
      <span className="sr-only">{t('nav.signIn')}</span>
    </header>
  );
}
